import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { checkLessonAccess } from '../../utils/checkLessonAccess';
import { saveLastLocation } from '../../utils/lastLocation';

// ✅ add these if you want Firestore persistence when logged in
import { auth, db } from '../../firebase';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';

const LOCAL_KEY = (lessonId) => `practiceCompleted_${lessonId}`;

const slugify = (s) =>
  (s || 'item')
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');

const LessonPractice = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [checkingAccess, setCheckingAccess] = useState(true);
  const [warning, setWarning] = useState('');

  const [practiceData, setPracticeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lessonTitle, setLessonTitle] = useState('');
  const [backgroundImage, setBackgroundImage] = useState('');

  const [fullscreenIframe, setFullscreenIframe] = useState(null);
  const [activeItem, setActiveItem] = useState(null); // current opened activity

  // { [exerciseId]: true }
  const [completed, setCompleted] = useState({});

  const completedCount = useMemo(
    () => Object.values(completed).filter(Boolean).length,
    [completed]
  );

  useEffect(() => {
    const verifyAccess = async () => {
      const lessonNum = parseInt(lessonId);
      const allowed = await checkLessonAccess(lessonNum);

      if (!allowed) {
        setWarning('🚫 This lesson is locked. Redirecting you to your dashboard...');
        setTimeout(() => {
          navigate('/dashboard');
        }, 2500);
      }

      setCheckingAccess(false);
    };

    verifyAccess();
  }, [lessonId, navigate]);

  // Save when this page is opened
  useEffect(() => {
    saveLastLocation(location.pathname);
  }, [location.pathname]);

  // Fetch practice JSON
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`/data/lesson${lessonId}-practice.json`);
        const data = await res.json();

        setLessonTitle(data.title);
        setBackgroundImage(data.backgroundImage);
        setPracticeData(data.wordwallExercises || []);
      } catch (error) {
        console.error('Error fetching practice data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [lessonId]);

  // Load completion (Firestore if logged-in, else localStorage)
  useEffect(() => {
    const load = async () => {
      try {
        const user = auth?.currentUser || null;
        if (user) {
          const ref = doc(db, 'users', user.uid, 'practice', lessonId);
          const snap = await getDoc(ref);
          if (snap.exists()) {
            setCompleted(snap.data().completed || {});
          } else {
            setCompleted({});
          }
        } else {
          const raw = localStorage.getItem(LOCAL_KEY(lessonId));
          setCompleted(raw ? JSON.parse(raw) : {});
        }
      } catch (e) {
        console.error('Failed to load completion map:', e);
        const raw = localStorage.getItem(LOCAL_KEY(lessonId));
        setCompleted(raw ? JSON.parse(raw) : {});
      }
    };
    load();
  }, [lessonId]);

  // Persist completion
  const persist = async (next) => {
    try {
      const user = auth?.currentUser || null;
      if (user) {
        const ref = doc(db, 'users', user.uid, 'practice', lessonId);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          await updateDoc(ref, { completed: next, updatedAt: Date.now() });
        } else {
          await setDoc(ref, { completed: next, createdAt: Date.now(), updatedAt: Date.now() });
        }
      } else {
        localStorage.setItem(LOCAL_KEY(lessonId), JSON.stringify(next));
      }
    } catch (e) {
      console.error('Failed to persist completion, falling back to localStorage:', e);
      localStorage.setItem(LOCAL_KEY(lessonId), JSON.stringify(next));
    }
  };

  const markDone = async (id) => {
    const next = { ...completed, [id]: true };
    setCompleted(next);
    await persist(next);
  };

  const resetOne = async (id) => {
    const next = { ...completed };
    delete next[id];
    setCompleted(next);
    await persist(next);
  };

  const openActivity = (exercise) => {
    const typeKey = exercise.title
      ? exercise.title.toLowerCase().split(' ')[0]
      : 'default';
    const id = exercise.id || exercise.key || slugify(exercise.title || exercise.embedUrl || typeKey);
    setActiveItem({ ...exercise, _id: id, _typeKey: typeKey });
    setFullscreenIframe(exercise.embedUrl);
  };

  const closeOverlay = () => {
    setFullscreenIframe(null);
    setActiveItem(null);
  };

  const handleNext = () => {
    navigate(`/lesson/${lessonId}/reading`);
  };

  if (checkingAccess) return <div>Checking access...</div>;
  if (warning) return <div style={{ padding: '40px', textAlign: 'center', color: 'crimson', fontSize: '18px' }}>{warning}</div>;
  if (loading) return <div>Loading Practice...</div>;

  return (
    <div className="lesson-practice-container">
      {/* ✅ Hero Image and Title */}
      <div
        className="hero-section"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '60px 20px',
          color: '#fff',
          textAlign: 'center'
        }}
      >
        <h1>{lessonTitle}</h1>

        {/* small progress pill */}
        <div className="practice-progress-pill" aria-label={`Completed ${completedCount} of ${practiceData.length}`}>
          {completedCount}/{practiceData.length} done
        </div>
      </div>

      {/* ✅ Practice Cards */}
      <section className="practice-section">
        <div className="practice-grid badge-style">
          {practiceData.map((exercise, index) => {
            const typeKey = exercise.title
              ? exercise.title.toLowerCase().split(' ')[0]
              : 'default';

            const id =
              exercise.id ||
              exercise.key ||
              slugify(exercise.title || exercise.embedUrl || `${typeKey}-${index}`);

            const isDone = !!completed[id];

            return (
              <div
                key={id}
                className={`practice-card badge-card ${isDone ? 'done' : ''}`}
                onClick={() => openActivity(exercise)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && openActivity(exercise)}
                aria-label={`Open ${exercise.title}${isDone ? ' (completed)' : ''}`}
              >
                {/* ✓ check bubble */}
                {isDone && (
                  <div className="practice-check" aria-hidden="true">✓</div>
                )}

                <div className="practice-badgeWrap">
                  <div className="practice-badgeContainer">
                    <img
                      src={`/images/practice/${typeKey}.png`}
                      alt={exercise.title}
                      className="practice-badgeImage"
                      onError={(e) => (e.currentTarget.style.display = 'none')}
                    />
                  </div>
                </div>

                <h3 className="practice-title">{exercise.title}</h3>

                <div className="practice-cta">
                  <span className="practice-buttonLike">{isDone ? 'Review' : 'Start'}</span>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 🔳 Fullscreen overlay with Mark as done / Reset */}
      {fullscreenIframe && activeItem && (
        <div className="fullscreen-overlay" role="dialog" aria-modal="true">
          <div className="practice-overlay-header">
            <div className="practice-overlay-title">
              <strong>{activeItem.title}</strong>
              {completed[activeItem._id] && <span className="practice-done-chip">Done</span>}
            </div>
            <div className="practice-overlay-actions">
              {!completed[activeItem._id] ? (
                <button className="mark-done-btn" onClick={() => markDone(activeItem._id)}>Mark as done</button>
              ) : (
                <button className="reset-btn" onClick={() => resetOne(activeItem._id)}>Reset</button>
              )}
              <button className="close-btn" onClick={closeOverlay}>Close</button>
            </div>
          </div>

          <div className="practice-overlay-body">
            <iframe
              src={fullscreenIframe}
              width="100%"
              height="100%"
              frameBorder="0"
              allowFullScreen
              title="Practice Activity"
              className="practice-iframe"
            />
          </div>
        </div>
      )}

      {/* ✅ Navigation */}
      <div style={styles.navigationButtons}>
        <button style={styles.navButton} onClick={handleNext}>
          Continue to Watch & Read →
        </button>
        <button style={styles.navButton} onClick={() => navigate(`/lesson/${lessonId}/vocab`)}>
          ← Back to Vocabulary
        </button>
        <button style={styles.navButton} onClick={() => navigate(`/dashboard`)}>
          Take a break & Go Back to Dashboard
        </button>
      </div>
    </div>
  );
};

export default LessonPractice;

const styles = {
  navigationButtons: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    marginTop: '40px',
    padding: '20px',
    borderTop: '2px solid #eee'
  },

  navButton: {
    padding: '12px 24px',
    fontSize: '18px',
    backgroundColor: '#0d6efd',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer'
  }
};
